/** @jsxImportSource @emotion/react */
import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ClockIcon } from '../../assets/icons/clock-icon';
import { workingHoursStyles } from './location-working-hours.styles';
import { IWorkHour } from '../../api/locations/types';

type PropTypes = {
    workingHours?: IWorkHour[];
};

const formatTime = (time?: string) => {
    if (!time) return '';
    return time;
};

const getHoursKey = (hour: IWorkHour) =>
    `${hour.isOpen}|${hour.is24Hours}|${hour.openTime ?? ''}|${hour.closeTime ?? ''}`;

export const LocationWorkingHours: FC<PropTypes> = ({ workingHours }) => {
    const intl = useIntl();

    const DAY_NAMES = [
        intl.formatMessage({ id: 'workingHours.sunday' }),
        intl.formatMessage({ id: 'workingHours.monday' }),
        intl.formatMessage({ id: 'workingHours.tuesday' }),
        intl.formatMessage({ id: 'workingHours.wednesday' }),
        intl.formatMessage({ id: 'workingHours.thursday' }),
        intl.formatMessage({ id: 'workingHours.friday' }),
        intl.formatMessage({ id: 'workingHours.saturday' }),
    ];

    const groupedHours = useMemo(() => {
        if (!workingHours || workingHours.length === 0) {
            return [];
        }

        const sortedHours = [...workingHours].sort((a, b) => a.day - b.day);

        return sortedHours.reduce<{ startDay: number; endDay: number; hour: IWorkHour }[]>(
            (groups, hour) => {
                const previousGroup = groups[groups.length - 1];

                if (!previousGroup) {
                    return [{ startDay: hour.day, endDay: hour.day, hour }];
                }

                const isConsecutiveDay = hour.day === previousGroup.endDay + 1;
                const hasSameHours = getHoursKey(hour) === getHoursKey(previousGroup.hour);

                if (isConsecutiveDay && hasSameHours) {
                    previousGroup.endDay = hour.day;
                    return groups;
                }

                return [...groups, { startDay: hour.day, endDay: hour.day, hour }];
            },
            []
        );
    }, [workingHours]);

    if (!workingHours?.length) {
        return null;
    }

    const renderHours = (hour: IWorkHour) => {
        if (!hour.isOpen) {
            return (
                <span css={workingHoursStyles.closed}>
                    {intl.formatMessage({ id: 'workingHours.closed' })}
                </span>
            );
        }

        if (hour.is24Hours) {
            return (
                <span css={workingHoursStyles.open24}>
                    {intl.formatMessage({ id: 'workingHours.24hours' })}
                </span>
            );
        }

        const openTime = formatTime(hour.openTime);
        const closeTime = formatTime(hour.closeTime);

        if (!openTime || !closeTime) {
            return <span css={workingHoursStyles.hours}>-</span>;
        }

        return <span css={workingHoursStyles.hours}>{`${openTime} - ${closeTime}`}</span>;
    };

    return (
        <div css={workingHoursStyles.wrapper}>
            <ClockIcon css={workingHoursStyles.icon} />
            <div css={workingHoursStyles.container}>
                {groupedHours?.map(group => (
                    <div
                        key={`${group.startDay}-${group.endDay}-${group.hour._id}`}
                        css={workingHoursStyles.dayRow}>
                        <span css={workingHoursStyles.dayName}>
                            {group.startDay === group.endDay
                                ? DAY_NAMES[group.startDay]
                                : `${DAY_NAMES[group.startDay]} - ${DAY_NAMES[group.endDay]}`}
                        </span>
                        {renderHours(group.hour)}
                    </div>
                ))}
            </div>
        </div>
    );
};
